import type { ConnectorDefinition, PartDefinition } from '../../catalog/part-definition'
import { getPartById } from '../../catalog/part-catalog'
import type { PlacedPart } from '../../domain/assembly'
import type { AssemblyConnection } from '../../domain/connection'

const METERS_TO_SCENE_UNITS = 20
const SNAP_DISTANCE = 0.44
const MOTOR_SNAP_DISTANCE = 0.58
const ALIGNED_CONNECTION_TOLERANCE = 0.03

type Point = { x: number; y: number; z: number }

function rotate(point: Point, rotation: PlacedPart['rotation']): Point {
  const cosX = Math.cos(rotation.x)
  const sinX = Math.sin(rotation.x)
  const cosY = Math.cos(rotation.y)
  const sinY = Math.sin(rotation.y)
  const cosZ = Math.cos(rotation.z)
  const sinZ = Math.sin(rotation.z)
  // Babylon applies Euler rotations as yaw(Y) * pitch(X) * roll(Z).
  const afterZ = {
    x: point.x * cosZ - point.y * sinZ,
    y: point.x * sinZ + point.y * cosZ,
    z: point.z,
  }
  const afterX = {
    x: afterZ.x,
    y: afterZ.y * cosX - afterZ.z * sinX,
    z: afterZ.y * sinX + afterZ.z * cosX,
  }
  return {
    x: afterX.x * cosY + afterX.z * sinY,
    y: afterX.y,
    z: -afterX.x * sinY + afterX.z * cosY,
  }
}

function cross(a: Point, b: Point): Point {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  }
}

function dot(a: Point, b: Point) {
  return a.x * b.x + a.y * b.y + a.z * b.z
}

function normalize(point: Point): Point {
  const length = Math.hypot(point.x, point.y, point.z) || 1
  return { x: point.x / length, y: point.y / length, z: point.z / length }
}

function connectorVector(connector: ConnectorDefinition, key: 'normal' | 'up'): Point {
  return { x: connector[key][0], y: connector[key][1], z: connector[key][2] }
}

type Matrix3 = [
  [number, number, number],
  [number, number, number],
  [number, number, number],
]

function rotationMatrix(rotation: PlacedPart['rotation']): Matrix3 {
  const right = rotate({ x: 1, y: 0, z: 0 }, rotation)
  const up = rotate({ x: 0, y: 1, z: 0 }, rotation)
  const forward = rotate({ x: 0, y: 0, z: 1 }, rotation)
  return [
    [right.x, up.x, forward.x],
    [right.y, up.y, forward.y],
    [right.z, up.z, forward.z],
  ]
}

function multiplyMatrices(a: Matrix3, b: Matrix3): Matrix3 {
  return Array.from({ length: 3 }, (_, row) =>
    Array.from({ length: 3 }, (_, column) =>
      a[row].reduce((sum, value, index) => sum + value * b[index][column], 0),
    ) as [number, number, number],
  ) as Matrix3
}

function transposeMatrix(matrix: Matrix3): Matrix3 {
  return [
    [matrix[0][0], matrix[1][0], matrix[2][0]],
    [matrix[0][1], matrix[1][1], matrix[2][1]],
    [matrix[0][2], matrix[1][2], matrix[2][2]],
  ]
}

function transformPoint(matrix: Matrix3, point: Point): Point {
  return {
    x: matrix[0][0] * point.x + matrix[0][1] * point.y + matrix[0][2] * point.z,
    y: matrix[1][0] * point.x + matrix[1][1] * point.y + matrix[1][2] * point.z,
    z: matrix[2][0] * point.x + matrix[2][1] * point.y + matrix[2][2] * point.z,
  }
}

function perpendicularUp(upValue: Point, normalValue: Point): Point {
  const normal = normalize(normalValue)
  const projection = dot(upValue, normal)
  const perpendicular = {
    x: upValue.x - normal.x * projection,
    y: upValue.y - normal.y * projection,
    z: upValue.z - normal.z * projection,
  }
  if (Math.hypot(perpendicular.x, perpendicular.y, perpendicular.z) > 0.000001) {
    return normalize(perpendicular)
  }
  return normalize(Math.abs(normal.y) < 0.9
    ? cross(normal, { x: 0, y: 1, z: 0 })
    : cross(normal, { x: 1, y: 0, z: 0 }))
}

function connectorBasis(normalValue: Point, upValue: Point): Matrix3 {
  const normal = normalize(normalValue)
  const up = perpendicularUp(upValue, normal)
  const right = normalize(cross(up, normal))
  return [
    [right.x, up.x, normal.x],
    [right.y, up.y, normal.y],
    [right.z, up.z, normal.z],
  ]
}

function rotationDistance(a: Matrix3, b: Matrix3) {
  const relative = multiplyMatrices(transposeMatrix(a), b)
  const cosine = Math.max(-1, Math.min(1, (
    relative[0][0] + relative[1][1] + relative[2][2] - 1
  ) / 2))
  return Math.acos(cosine)
}

function axisAngleMatrix(axisValue: Point, angle: number): Matrix3 {
  const axis = normalize(axisValue)
  const cosine = Math.cos(angle)
  const sine = Math.sin(angle)
  const oneMinusCosine = 1 - cosine
  const { x, y, z } = axis
  return [
    [
      cosine + x * x * oneMinusCosine,
      x * y * oneMinusCosine - z * sine,
      x * z * oneMinusCosine + y * sine,
    ],
    [
      y * x * oneMinusCosine + z * sine,
      cosine + y * y * oneMinusCosine,
      y * z * oneMinusCosine - x * sine,
    ],
    [
      z * x * oneMinusCosine - y * sine,
      z * y * oneMinusCosine + x * sine,
      cosine + z * z * oneMinusCosine,
    ],
  ]
}

function matrixToRotation(matrix: Matrix3): PlacedPart['rotation'] {
  const sineX = Math.max(-1, Math.min(1, -matrix[1][2]))
  const x = Math.asin(sineX)
  if (Math.abs(Math.cos(x)) < 0.000001) {
    const y = sineX > 0
      ? Math.atan2(matrix[0][1], matrix[0][0])
      : Math.atan2(-matrix[0][1], matrix[0][0])
    return {
      x,
      y,
      z: 0,
    }
  }
  return {
    x,
    y: Math.atan2(matrix[0][2], matrix[2][2]),
    z: Math.atan2(matrix[1][0], matrix[1][1]),
  }
}

function normalAlignedRotation(
  movingPart: PlacedPart,
  movingConnector: ConnectorDefinition,
  targetPart: PlacedPart,
  targetConnector: ConnectorDefinition,
): PlacedPart['rotation'] {
  const targetNormal = normalize(worldNormal(targetPart, targetConnector))
  const desiredNormal = { x: -targetNormal.x, y: -targetNormal.y, z: -targetNormal.z }
  const sourceBasis = connectorBasis(
    connectorVector(movingConnector, 'normal'),
    connectorVector(movingConnector, 'up'),
  )
  const targetUp = perpendicularUp(
    rotate(connectorVector(targetConnector, 'up'), targetPart.rotation),
    desiredNormal,
  )
  const currentRotation = rotationMatrix(movingPart.rotation)
  const rotationSteps = Math.max(1, Math.min(
    movingConnector.rotationSteps,
    targetConnector.rotationSteps,
  ))

  let nearestMatrix: Matrix3 | null = null
  let nearestDistance = Number.POSITIVE_INFINITY
  for (let index = 0; index < rotationSteps; index += 1) {
    const steppedUp = transformPoint(
      axisAngleMatrix(desiredNormal, index * Math.PI * 2 / rotationSteps),
      targetUp,
    )
    const targetBasis = connectorBasis(desiredNormal, steppedUp)
    const candidate = multiplyMatrices(targetBasis, transposeMatrix(sourceBasis))
    const distance = rotationDistance(currentRotation, candidate)
    if (distance >= nearestDistance) continue
    nearestDistance = distance
    nearestMatrix = candidate
  }

  return matrixToRotation(nearestMatrix ?? currentRotation)
}

export function connectorWorldPosition(part: PlacedPart, connector: ConnectorDefinition): Point {
  const local = rotate({
    x: connector.position[0] * METERS_TO_SCENE_UNITS,
    y: connector.position[1] * METERS_TO_SCENE_UNITS,
    z: connector.position[2] * METERS_TO_SCENE_UNITS,
  }, part.rotation)
  return {
    x: part.position.x + local.x,
    y: part.position.y + local.y,
    z: part.position.z + local.z,
  }
}

function worldNormal(part: PlacedPart, connector: ConnectorDefinition): Point {
  return rotate({
    x: connector.normal[0],
    y: connector.normal[1],
    z: connector.normal[2],
  }, part.rotation)
}

function compatible(a: ConnectorDefinition, b: ConnectorDefinition) {
  return a.allowedTypes.includes(b.type) && b.allowedTypes.includes(a.type)
}

function isMotorPair(a: ConnectorDefinition, b: ConnectorDefinition) {
  return (a.type === 'motor-male' && b.type === 'motor-female')
    || (a.type === 'motor-female' && b.type === 'motor-male')
}

function isCrossPair(a: ConnectorDefinition, b: ConnectorDefinition) {
  return (a.type === 'cross-male' && b.type === 'cross-female')
    || (a.type === 'cross-female' && b.type === 'cross-male')
}

function isKeyedPair(a: ConnectorDefinition, b: ConnectorDefinition) {
  return isMotorPair(a, b) || isCrossPair(a, b)
}

function facingEachOther(
  movingPart: PlacedPart,
  movingConnector: ConnectorDefinition,
  targetPart: PlacedPart,
  targetConnector: ConnectorDefinition,
) {
  const a = worldNormal(movingPart, movingConnector)
  const b = worldNormal(targetPart, targetConnector)
  return a.x * b.x + a.y * b.y + a.z * b.z < -0.8
}

function connectorIsUsed(
  connections: AssemblyConnection[],
  partId: string,
  connectorId: string,
) {
  return connections.some((connection) =>
    (connection.fromPartId === partId && connection.fromConnectorId === connectorId)
    || (connection.toPartId === partId && connection.toConnectorId === connectorId))
}

export type SnapResult = {
  position: PlacedPart['position']
  rotation: PlacedPart['rotation']
  connection: AssemblyConnection
  connections: AssemblyConnection[]
  distance: number
}

function jointTypeFor(
  movingConnector: ConnectorDefinition,
  targetConnector: ConnectorDefinition,
): AssemblyConnection['jointType'] {
  const isC103MotorPair = (
    movingConnector.id === 'motor-male-fork'
    && targetConnector.type === 'motor-female'
  ) || (
    targetConnector.id === 'motor-male-fork'
    && movingConnector.type === 'motor-female'
  ) || (
    movingConnector.id === 'cross-male-fork'
    && targetConnector.type === 'cross-female'
  ) || (
    targetConnector.id === 'cross-male-fork'
    && movingConnector.type === 'cross-female'
  )

  return isC103MotorPair
    || movingConnector.id.includes('wheel')
    || targetConnector.id.includes('wheel')
    || movingConnector.type === 'axle'
    || targetConnector.type === 'wheel-hub'
    ? 'revolute'
    : 'fixed'
}

function createConnection(
  movingPart: PlacedPart,
  movingConnector: ConnectorDefinition,
  targetPart: PlacedPart,
  targetConnector: ConnectorDefinition,
): AssemblyConnection {
  return {
    id: `connection-${crypto.randomUUID()}`,
    fromPartId: movingPart.instanceId,
    fromConnectorId: movingConnector.id,
    toPartId: targetPart.instanceId,
    toConnectorId: targetConnector.id,
    jointType: jointTypeFor(movingConnector, targetConnector),
  }
}

function collectAlignedConnections(
  movingPart: PlacedPart,
  movingDefinition: PartDefinition,
  allParts: PlacedPart[],
  existingConnections: AssemblyConnection[],
  primaryConnection: AssemblyConnection,
) {
  const collected = [primaryConnection]
  const usedMovingConnectors = new Set([primaryConnection.fromConnectorId])
  const usedTargetConnectors = new Set([
    `${primaryConnection.toPartId}:${primaryConnection.toConnectorId}`,
  ])

  for (const movingConnector of movingDefinition.connectors) {
    if (usedMovingConnectors.has(movingConnector.id)) continue
    if (connectorIsUsed(existingConnections, movingPart.instanceId, movingConnector.id)) continue

    let nearest: { part: PlacedPart; connector: ConnectorDefinition; distance: number } | null = null
    for (const targetPart of allParts) {
      if (targetPart.instanceId === movingPart.instanceId) continue
      const targetDefinition = getPartById(targetPart.partId)
      if (!targetDefinition) continue
      for (const targetConnector of targetDefinition.connectors) {
        const targetKey = `${targetPart.instanceId}:${targetConnector.id}`
        if (usedTargetConnectors.has(targetKey)) continue
        if (!compatible(movingConnector, targetConnector)) continue
        if (connectorIsUsed(existingConnections, targetPart.instanceId, targetConnector.id)) continue
        if (!facingEachOther(movingPart, movingConnector, targetPart, targetConnector)) continue
        const movingWorld = connectorWorldPosition(movingPart, movingConnector)
        const targetWorld = connectorWorldPosition(targetPart, targetConnector)
        const distance = Math.hypot(
          targetWorld.x - movingWorld.x,
          targetWorld.y - movingWorld.y,
          targetWorld.z - movingWorld.z,
        )
        if (distance > ALIGNED_CONNECTION_TOLERANCE || (nearest && nearest.distance <= distance)) continue
        nearest = { part: targetPart, connector: targetConnector, distance }
      }
    }
    if (!nearest) continue
    usedMovingConnectors.add(movingConnector.id)
    usedTargetConnectors.add(`${nearest.part.instanceId}:${nearest.connector.id}`)
    collected.push(createConnection(movingPart, movingConnector, nearest.part, nearest.connector))
  }
  return collected
}

function c103PairIsComplete(
  movingPart: PlacedPart,
  allParts: PlacedPart[],
  existingConnections: AssemblyConnection[],
  candidateConnections: AssemblyConnection[],
) {
  const partsByInstanceId = new Map(allParts.map((part) => [part.instanceId, part]))
  partsByInstanceId.set(movingPart.instanceId, movingPart)
  const c103InstanceIds = new Set(
    candidateConnections.flatMap((connection) => [connection.fromPartId, connection.toPartId])
      .filter((instanceId) =>
        partsByInstanceId.get(instanceId)?.partId === 'part-link-c1-c1-03'),
  )
  if (!c103InstanceIds.size) return true

  const allConnections = [...existingConnections, ...candidateConnections]
  for (const c103InstanceId of c103InstanceIds) {
    const connectionsByConnector = new Map<string, { counterpartId: string; counterpartConnectorId: string }>()
    for (const connection of allConnections) {
      if (connection.fromPartId === c103InstanceId) {
        connectionsByConnector.set(connection.fromConnectorId, {
          counterpartId: connection.toPartId,
          counterpartConnectorId: connection.toConnectorId,
        })
      } else if (connection.toPartId === c103InstanceId) {
        connectionsByConnector.set(connection.toConnectorId, {
          counterpartId: connection.fromPartId,
          counterpartConnectorId: connection.fromConnectorId,
        })
      }
    }
    const motor = connectionsByConnector.get('motor-male-fork')
    const crossMount = connectionsByConnector.get('cross-male-fork')
    if (!motor || !crossMount || motor.counterpartId !== crossMount.counterpartId) return false
    if (partsByInstanceId.get(motor.counterpartId)?.partId !== 'part-cube-stl-step') return false
    if (motor.counterpartConnectorId !== 'motor-female-front') return false
    if (!crossMount.counterpartConnectorId.startsWith('cross-female-')) return false
  }
  return true
}

export function findBestSnap(
  movingPart: PlacedPart,
  allParts: PlacedPart[],
  connections: AssemblyConnection[],
): SnapResult | null {
  const movingDefinition = getPartById(movingPart.partId)
  if (!movingDefinition) return null

  let best: SnapResult | null = null
  for (const movingConnector of movingDefinition.connectors) {
    if (connectorIsUsed(connections, movingPart.instanceId, movingConnector.id)) continue
    for (const targetPart of allParts) {
      if (targetPart.instanceId === movingPart.instanceId) continue
      const targetDefinition = getPartById(targetPart.partId)
      if (!targetDefinition) continue

      for (const targetConnector of targetDefinition.connectors) {
        if (!compatible(movingConnector, targetConnector)) continue
        if (connectorIsUsed(connections, targetPart.instanceId, targetConnector.id)) continue
        const currentMovingWorld = connectorWorldPosition(movingPart, movingConnector)
        const targetWorld = connectorWorldPosition(targetPart, targetConnector)
        const approachDistance = Math.hypot(
          targetWorld.x - currentMovingWorld.x,
          targetWorld.y - currentMovingWorld.y,
          targetWorld.z - currentMovingWorld.z,
        )
        const rotations = isKeyedPair(movingConnector, targetConnector)
          ? [normalAlignedRotation(movingPart, movingConnector, targetPart, targetConnector)]
          : [movingPart.rotation]
        for (const rotation of rotations) {
          const orientedMovingPart = { ...movingPart, rotation }
          if (!facingEachOther(orientedMovingPart, movingConnector, targetPart, targetConnector)) continue

          const movingWorld = connectorWorldPosition(orientedMovingPart, movingConnector)
          const dx = targetWorld.x - movingWorld.x
          const dy = targetWorld.y - movingWorld.y
          const dz = targetWorld.z - movingWorld.z
          const placementDistance = Math.hypot(dx, dy, dz)
          const snapDistance = isMotorPair(movingConnector, targetConnector)
            ? MOTOR_SNAP_DISTANCE
            : SNAP_DISTANCE
          if (placementDistance > snapDistance || (best && best.distance <= approachDistance)) continue

          const connection = createConnection(
            movingPart,
            movingConnector,
            targetPart,
            targetConnector,
          )
          const candidate: SnapResult = {
            distance: approachDistance,
            position: {
              x: Number((movingPart.position.x + dx).toFixed(4)),
              y: Number((movingPart.position.y + dy).toFixed(4)),
              z: Number((movingPart.position.z + dz).toFixed(4)),
            },
            rotation,
            connection,
            connections: [connection],
          }
          const snappedPart = {
            ...movingPart,
            position: candidate.position,
            rotation: candidate.rotation,
          }
          candidate.connections = collectAlignedConnections(
            snappedPart,
            movingDefinition,
            allParts,
            connections,
            connection,
          )
          if (!c103PairIsComplete(
            snappedPart,
            allParts,
            connections,
            candidate.connections,
          )) continue
          best = candidate
        }
      }
    }
  }
  return best
}

export function connectorsForPart(part: PartDefinition) {
  return part.connectors
}
